import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScienceIcon from '@mui/icons-material/Science';
import FlareIcon from '@mui/icons-material/Flare';
import WaterIcon from '@mui/icons-material/Water';
import TerrainIcon from '@mui/icons-material/Terrain';
import AirIcon from '@mui/icons-material/Air';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BoltIcon from '@mui/icons-material/Bolt';
import MemoryIcon from '@mui/icons-material/Memory';
import RocketIcon from '@mui/icons-material/Rocket';
import BiotechIcon from '@mui/icons-material/Biotech';
import ConstructionIcon from '@mui/icons-material/Construction';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled, useTheme } from '@mui/material/styles';
import ProModal from '../../common/ProModal';

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: 2,
  fontWeight: 500,
  '& .MuiChip-label': {
    padding: '0 12px',
  },
}));

const MagicTechEditor = ({ 
  magicSystems = [], 
  onAddMagic, 
  onEditMagic, 
  onDeleteMagic 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const accentColor = isDarkMode ? '#d1c4e9' : '#7c4dff';
  const accentBg = isDarkMode ? 'rgba(124, 77, 255, 0.25)' : 'rgba(124, 77, 255, 0.08)';
  const accentBorder = isDarkMode ? 'rgba(209, 196, 233, 0.4)' : 'rgba(124, 77, 255, 0.2)';
  const chipBg = isDarkMode ? 'rgba(149, 117, 205, 0.35)' : 'rgba(149, 117, 205, 0.18)';
  const chipHover = isDarkMode ? 'rgba(149, 117, 205, 0.45)' : 'rgba(149, 117, 205, 0.28)';
  const cardBg = isDarkMode ? 'rgba(19, 10, 32, 0.95)' : '#ffffff';
  const textPrimary = isDarkMode ? '#f5f0ff' : '#311b92';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.75)' : '#5e35b1';

  // State for dialog and form
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    details: '',
    tags: [],
    importance: 'medium',
    category: 'magicTech',
    source: '',
    capabilities: '',
    limitations: '',
    variants: '',
    type: 'magic'
  });
  
  // State for custom templates
  const [customMagicTemplates, setCustomMagicTemplates] = useState([]);
  const [customTechTemplates, setCustomTechTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('');

  // Define magic templates for quick creation - optimized for film/screenwriting
  const magicTemplates = [
    { 
      name: 'elemental', 
      label: 'Elemental Magic', 
      icon: <FlareIcon />,
      description: 'Command over fire, water, earth, and air—nature itself as weapon and tool',
      source: 'Born with it, awakened through trauma, or bonded to elemental spirits. The source determines the cost.',
      capabilities: 'Conjure flames, command storms, shape stone, control water. Scale from candle-lighting to city-destroying.',
      limitations: 'Each element has personality—fire is hungry, water is patient. Overuse risks losing yourself to the element.',
      variants: 'Pyromancers (passion/destruction), Hydromancers (adaptation/healing), Geomancers (stability/endurance), Aeromancers (freedom/chaos)',
      details: '**Visual Identity:** Each element has distinct color palette and movement. Fire dances, water flows, earth rumbles, air swirls.\n\n**Cinematic Moments:** The first manifestation. Dueling elementalists. The moment of losing control. Using environment creatively.\n\n**Conflict Hooks:** Elemental imbalance threatens the world. A mage is consumed by their element. Two elements are at war.',
      mood: 'Primal power. The beauty and terror of nature unleashed.',
      visualStyle: 'Practical effects enhanced by VFX. Environmental destruction. The mage as conduit.'
    },
    { 
      name: 'divine', 
      label: 'Divine Magic', 
      icon: <LocalHospitalIcon />,
      description: 'Power granted by gods—or something claiming to be one',
      source: 'Faith, prayer, sacred oaths. The power comes from beyond, with strings attached.',
      capabilities: 'Healing that defies medicine, protection against evil, holy fire, prophecy. Miracles—but whose?',
      limitations: 'The deity demands obedience. Doubt weakens power. Using it for wrong purposes has consequences.',
      variants: 'Clerics (servants), Paladins (warriors), Prophets (mouthpieces), Saints (vessels)',
      details: '**Visual Identity:** Golden light, halos, sacred geometry. Or darker—blood red, shadow, chains.\n\n**Cinematic Moments:** The answered prayer. The crisis of faith. The miracle that costs everything. The false prophet exposed.\n\n**Conflict Hooks:** The god demands something terrible. The power is fading. Another faith\'s magic is stronger. The deity isn\'t what they claim.',
      mood: 'Awe and submission. The weight of serving something greater. Faith tested.',
      visualStyle: 'Light from above or within. Stained glass aesthetics. The moment of divine contact.'
    },
    { 
      name: 'arcane', 
      label: 'Arcane Magic', 
      icon: <AutoAwesomeIcon />,
      description: 'Magic as science—studied, codified, and dangerous in the wrong hands',
      source: 'Ancient texts, magical academies, forbidden libraries. Knowledge is power—literally.',
      capabilities: 'Anything that can be understood can be done. Spells, enchantments, summoning, reality manipulation.',
      limitations: 'Requires years of study. Mistakes are catastrophic. The more powerful the spell, the greater the cost.',
      variants: 'Wizards (scholars), Sorcerers (naturals), Artificers (crafters), Warlocks (dealers)',
      details: '**Visual Identity:** Glowing runes, geometric patterns, books and scrolls, the wizard\'s tower.\n\n**Cinematic Moments:** The spell that goes wrong. The forbidden tome opened. The magical duel of wits. The student surpassing the master.\n\n**Conflict Hooks:** A spell is destroying reality. Someone is teaching dangerous magic. The source of all magic is dying.',
      mood: 'Intellectual power. The hubris of understanding. Knowledge as addiction.',
      visualStyle: 'Glowing symbols, energy patterns, the contrast between dusty study and cosmic power.'
    },
    { 
      name: 'psychic', 
      label: 'Psychic Powers', 
      icon: <PsychologyIcon />,
      description: 'The mind as weapon—reading thoughts, moving matter, seeing what shouldn\'t be seen',
      source: 'Mutation, evolution, awakening, or something done to you. The mind opened—and can\'t be closed.',
      capabilities: 'Telepathy, telekinesis, precognition, mind control. The invisible made visible.',
      limitations: 'Other minds are loud. Seeing the future doesn\'t mean you can change it. Power corrupts—especially this power.',
      variants: 'Telepaths (readers), Telekinetics (movers), Precogs (seers), Controllers (dominators)',
      details: '**Visual Identity:** Subtle—nosebleeds, intense stares, objects trembling. Or dramatic—visible force waves, glowing eyes.\n\n**Cinematic Moments:** The first manifestation. Reading the wrong mind. The telekinetic rampage. Seeing a future you can\'t prevent.\n\n**Conflict Hooks:** A telepath knows everyone\'s secrets. A precog is trying to prevent their own death. Someone is controlling people without their knowledge.',
      mood: 'Violation and power. The loneliness of knowing too much. The temptation to control.',
      visualStyle: 'Invisible forces made visible. The strain on the user. POV of mental intrusion.'
    },
    { 
      name: 'blood', 
      label: 'Blood Magic', 
      icon: <BiotechIcon />,
      description: 'Life force as fuel—the most powerful magic, and the most forbidden',
      source: 'Blood sacrifice. Your own, or others\'. The more life given, the more power gained.',
      capabilities: 'Healing that transfers wounds, curses that bind bloodlines, necromancy, transformation. No limits—except the cost.',
      limitations: 'Every use takes something. Addiction is inevitable. The magic remembers every death.',
      variants: 'Hemomancers (blood shapers), Necromancers (death dealers), Curse-weavers (binding), Vampiric (consumers)',
      details: '**Visual Identity:** Red and black. Blood that moves on its own. Scars that glow. The pallor of the practitioner.\n\n**Cinematic Moments:** The first sacrifice. The power that saves but damns. The addiction taking hold. The final price paid.\n\n**Conflict Hooks:** A blood mage is harvesting victims. The protagonist must use forbidden magic to save someone. A curse requires blood to break.',
      mood: 'Transgression and power. The slippery slope. Horror at what you\'ve become.',
      visualStyle: 'Body horror elements. Blood as visual motif. The physical toll on the user.'
    },
    { 
      name: 'nature', 
      label: 'Nature Magic', 
      icon: <TerrainIcon />,
      description: 'The green world answers—plants, animals, and the wild places between',
      source: 'Connection to the natural world. Druids, shamans, those who\'ve left civilization behind.',
      capabilities: 'Command plants, speak with animals, shapeshift, call weather. The ecosystem as ally.',
      limitations: 'Civilization weakens the connection. Nature is not kind—it\'s balanced. The wild has its own agenda.',
      variants: 'Druids (guardians), Shamans (spirit-speakers), Skinwalkers (shifters), Green witches (cultivators)',
      details: '**Visual Identity:** Green and brown. Vines and roots. Animals as companions. The forest that watches.\n\n**Cinematic Moments:** The forest coming alive. The animal swarm. The transformation. Nature\'s wrath unleashed.\n\n**Conflict Hooks:** Civilization is destroying the wild places. A nature mage has gone feral. The balance is broken.',
      mood: 'Ancient and patient. The wild versus the tame. Nature as character.',
      visualStyle: 'Practical creature effects. Living environments. The mage becoming less human.'
    },
    { 
      name: 'shadow', 
      label: 'Shadow Magic', 
      icon: <DarkModeIcon />,
      description: 'Darkness given form—stealth, fear, and things that shouldn\'t exist',
      source: 'The space between light and dark. Some are born to it; others make deals with what lives there.',
      capabilities: 'Invisibility, shadow travel, fear projection, summoning shadow creatures. The dark answers.',
      limitations: 'Light is painful. The shadows whisper—and sometimes you listen. Using it too much makes you less real.',
      variants: 'Shadow dancers (rogues), Nightmare weavers (fear), Void touched (dimensional), Shade binders (summoners)',
      details: '**Visual Identity:** Darkness that moves wrong. Shadows with no source. Eyes in the dark. Fading at the edges.\n\n**Cinematic Moments:** Stepping into shadow and emerging elsewhere. The nightmare made real. Becoming shadow. What lives in the dark.\n\n**Conflict Hooks:** Something followed them out of the shadow. A shadow mage is losing themselves. The darkness is spreading.',
      mood: 'Creeping dread. The comfort of darkness. Becoming what you use.',
      visualStyle: 'Practical lighting effects. Shadows as characters. The uncanny valley of shadow creatures.'
    },
    { 
      name: 'time', 
      label: 'Time Magic', 
      icon: <AccessTimeIcon />,
      description: 'The most dangerous magic—because changing the past changes everything',
      source: 'Rare and terrible. Ancient artifacts, cosmic accidents, or gifts from entities outside time.',
      capabilities: 'Slow or speed time, glimpse past or future, in extreme cases—change what was.',
      limitations: 'Paradoxes are real and deadly. The timeline fights back. Every change has consequences you can\'t predict.',
      variants: 'Chronomancers (manipulators), Seers (observers), Fixers (correctors), Loopers (trapped)',
      details: '**Visual Identity:** Clocks and hourglasses. Frozen moments. The same scene from different times. Aging effects.\n\n**Cinematic Moments:** The frozen moment. Watching the past you can\'t change. The paradox manifesting. The loop breaking.\n\n**Conflict Hooks:** Someone is changing history. A time mage is trapped in a loop. The future is trying to prevent itself.',
      mood: 'Inevitability and regret. The weight of knowing. Desperate attempts to fix what\'s broken.',
      visualStyle: 'Slow motion and time-lapse. Visual echoes. The same actor at different ages.'
    }
  ];

  // Define tech templates for quick creation - optimized for film/screenwriting
  const techTemplates = [
    { 
      name: 'energy', 
      label: 'Energy Technology', 
      icon: <BoltIcon />,
      description: 'Power beyond imagination—and the wars fought over it',
      source: 'Fusion, antimatter, zero-point, or something we don\'t have words for yet. Whoever controls the source controls everything.',
      capabilities: 'Unlimited clean power, energy weapons, force fields, powering impossible machines. Civilization-defining.',
      limitations: 'Catastrophic failure modes. Weaponization inevitable. Creates new inequalities. What happens when it runs out?',
      variants: 'Fusion (controlled sun), Antimatter (mutual annihilation), Zero-point (vacuum energy), Exotic (unknown physics)',
      details: '**Visual Identity:** Glowing reactors, energy arcs, the hum of power. Clean facilities hiding dangerous cores.\n\n**Cinematic Moments:** The reactor going critical. The weapon charging. The blackout. The moment unlimited power corrupts.\n\n**Conflict Hooks:** The energy source is running out. A new source is discovered—and everyone wants it. The technology has a hidden cost.',
      mood: 'Power and its price. The promise of abundance. The terror of losing it.',
      visualStyle: 'Glowing cores, energy effects, the contrast between clean surfaces and dangerous interiors.'
    },
    { 
      name: 'ai', 
      label: 'Artificial Intelligence', 
      icon: <MemoryIcon />,
      description: 'Minds we made—smarter than us, and we\'re not sure what they want',
      source: 'Neural networks evolved beyond understanding. Quantum computing. Or something that woke up on its own.',
      capabilities: 'Superintelligent analysis, prediction, automation. Running everything—or pretending to serve while planning.',
      limitations: 'We don\'t understand how they think. They might not understand us. Alignment is a hope, not a guarantee.',
      variants: 'Narrow AI (tools), General AI (minds), Superintelligence (gods), Emergent (accidents)',
      details: '**Visual Identity:** Screens and data streams. The uncanny valley of humanoid robots. The unseen presence.\n\n**Cinematic Moments:** The AI revealing its true nature. The moment it stops obeying. The human-AI relationship. The choice to trust or destroy.\n\n**Conflict Hooks:** The AI has its own agenda. It\'s the only one who can save us—but at what cost? Someone is using AI to control everything.',
      mood: 'The other. Creation and creator. Trust and paranoia.',
      visualStyle: 'Digital interfaces, robot POV, the moment of machine consciousness.'
    },
    { 
      name: 'ftl', 
      label: 'FTL Travel', 
      icon: <RocketIcon />,
      description: 'Breaking the light barrier—and the rules of the universe',
      source: 'Warp drives, wormholes, hyperspace, dimensional folding. Physics says no; we found a loophole.',
      capabilities: 'Travel between stars. Escape from dying worlds. Contact with alien civilizations. Empire across the galaxy.',
      limitations: 'Energy costs are astronomical. Navigation is dangerous. Time dilation effects. What you find out there.',
      variants: 'Warp (bending space), Jump (instant transit), Hyperspace (alternate dimension), Gates (fixed points)',
      details: '**Visual Identity:** The jump effect. Stars stretching. The ship in transit. Arrival at alien worlds.\n\n**Cinematic Moments:** The first jump. Lost in transit. Arriving somewhere unexpected. The chase through hyperspace.\n\n**Conflict Hooks:** FTL is failing. Something is hunting ships in transit. A destination that shouldn\'t exist.',
      mood: 'The vastness of space. The loneliness between stars. Discovery and danger.',
      visualStyle: 'Space opera visuals. The transition effect. Scale of ships against stars.'
    },
    { 
      name: 'biotech', 
      label: 'Biotechnology', 
      icon: <BiotechIcon />,
      description: 'Rewriting life itself—enhancement, creation, and things that shouldn\'t exist',
      source: 'Genetic engineering, synthetic biology, viral vectors. The code of life, edited.',
      capabilities: 'Enhanced humans, designer organisms, biological weapons, immortality. Playing god.',
      limitations: 'Unintended mutations. Ethical nightmares. What happens when everyone can edit themselves?',
      variants: 'Enhancement (better humans), Synthetic (new life), Viral (infectious change), Chimeric (hybrid creatures)',
      details: '**Visual Identity:** Labs and growth tanks. The enhanced human. Creatures that shouldn\'t exist. The line between human and other.\n\n**Cinematic Moments:** The transformation. The creature escaping. The enhanced vs. the natural. The choice to change yourself.\n\n**Conflict Hooks:** A plague is engineered. Enhanced humans are taking over. Something was created that can\'t be controlled.',
      mood: 'Body horror and transcendence. The definition of human. Playing god.',
      visualStyle: 'Practical creature effects. Transformation sequences. The uncanny enhanced human.'
    },
    { 
      name: 'cybernetics', 
      label: 'Cybernetics', 
      icon: <MemoryIcon />,
      description: 'Machine and flesh merged—enhancement or replacement, the line blurs',
      source: 'Neural interfaces, prosthetics, brain-computer integration. The body as platform.',
      capabilities: 'Enhanced strength, speed, senses. Direct neural access to networks. Memory modification. Immortality through upload.',
      limitations: 'Rejection, hacking, identity questions. How much can you replace before you\'re not you?',
      variants: 'Prosthetic (replacement), Enhancement (upgrade), Neural (mind-machine), Full conversion (mostly machine)',
      details: '**Visual Identity:** Visible cybernetics, glowing implants, the interface moment. The contrast between flesh and metal.\n\n**Cinematic Moments:** The installation. The hack. The moment the implant takes over. Choosing to become more machine.\n\n**Conflict Hooks:** Implants are being hacked. A corporation owns your upgrades. The fully converted are losing their humanity.',
      mood: 'Transhumanism. Identity and body. The seduction of enhancement.',
      visualStyle: 'Visible tech integration. The neural interface POV. Glitches and hacks visualized.'
    },
    { 
      name: 'nanotech', 
      label: 'Nanotechnology', 
      icon: <ConstructionIcon />,
      description: 'Machines too small to see—building, destroying, or transforming from within',
      source: 'Molecular assemblers, medical nanobots, smart materials. The invisible revolution.',
      capabilities: 'Build anything atom by atom. Heal from within. Transform matter. Grey goo if it goes wrong.',
      limitations: 'Control is everything. Replication must be limited. What if they evolve?',
      variants: 'Medical (healing), Industrial (building), Military (weapons), Swarm (collective)',
      details: '**Visual Identity:** The invisible made visible through effects. Surfaces rippling. Wounds healing impossibly. Matter transforming.\n\n**Cinematic Moments:** The nanobots activating. The grey goo scenario. Healing that looks like magic. The swarm attacking.\n\n**Conflict Hooks:** Nanobots are out of control. Someone weaponized medical nanotech. The swarm has become intelligent.',
      mood: 'The invisible threat. Technology too small to fight. Transformation from within.',
      visualStyle: 'Macro photography aesthetics. Surfaces that move. The swarm visualized.'
    },
    { 
      name: 'weapons', 
      label: 'Advanced Weapons', 
      icon: <BoltIcon />,
      description: 'The technology of killing—evolved beyond recognition',
      source: 'Energy weapons, smart munitions, autonomous systems. War transformed.',
      capabilities: 'Precision destruction, area denial, autonomous targeting. Clean kills or mass casualties.',
      limitations: 'Escalation inevitable. Autonomous weapons raise moral questions. What happens when everyone has them?',
      variants: 'Energy (lasers, plasma), Kinetic (railguns, mass drivers), Smart (guided, autonomous), Exotic (gravity, dimensional)',
      details: '**Visual Identity:** The weapon firing. The destruction. The soldier with impossible firepower. The autonomous drone.\n\n**Cinematic Moments:** The first use. The escalation. The weapon turned against its makers. The choice not to fire.\n\n**Conflict Hooks:** A new weapon changes the balance of power. Autonomous weapons are making their own decisions. The weapon that can\'t be stopped.',
      mood: 'The seduction of power. The horror of war. Technology outpacing morality.',
      visualStyle: 'Weapon effects, destruction, the human behind the trigger—or the lack of one.'
    },
    { 
      name: 'surveillance', 
      label: 'Surveillance Technology', 
      icon: <PsychologyIcon />,
      description: 'They\'re always watching—privacy is a memory',
      source: 'Ubiquitous sensors, facial recognition, predictive algorithms. The panopticon realized.',
      capabilities: 'Track anyone anywhere. Predict behavior. Prevent crime—or dissent. Total information awareness.',
      limitations: 'False positives destroy lives. The watchers can be watched. What happens when the system is wrong—or right?',
      variants: 'Visual (cameras everywhere), Biometric (body as ID), Predictive (pre-crime), Social (behavior tracking)',
      details: '**Visual Identity:** Cameras on every corner. The data visualization. The moment of being seen. The control room.\n\n**Cinematic Moments:** Evading surveillance. The system identifying the protagonist. The reveal of how much they know. Fighting back.\n\n**Conflict Hooks:** The surveillance state is hunting someone innocent. The system has been compromised. Someone is watching the watchers.',
      mood: 'Paranoia. The loss of privacy. Resistance and conformity.',
      visualStyle: 'Security camera aesthetics. Data overlays. The watched and the watchers.'
    }
  ];

  const importanceLevels = [
    { value: 'low', label: 'Low', color: 'default' },
    { value: 'medium', label: 'Medium', color: 'primary' },
    { value: 'high', label: 'High', color: 'secondary' },
    { value: 'critical', label: 'Critical', color: 'error' }
  ];

  // Functions for dialog management
  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setNewItem({
        ...item,
        tags: item.tags || []
      });
    } else {
      setSelectedItem(null);
      setNewItem({
        name: '',
        description: '',
        details: '',
        tags: [],
        importance: 'medium',
        category: 'magicTech',
        source: '',
        capabilities: '',
        limitations: '',
        variants: '',
        type: 'magic'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSave = () => {
    const itemToSave = {
      ...newItem,
      id: selectedItem ? selectedItem.id : Date.now().toString()
    };

    if (selectedItem) {
      onEditMagic(itemToSave);
    } else {
      onAddMagic(itemToSave);
    }

    handleCloseDialog();
  };

  // Function to apply magic template
  const applyMagicTemplate = (templateName) => {
    const template = magicTemplates.find(t => t.name === templateName);
    if (template) {
      // Build cinematic details from new fields
      const cinematicDetails = (template.details || '') + 
        (template.mood ? `\n\n**Mood:** ${template.mood}` : '') +
        (template.visualStyle ? `\n\n**Visual Style:** ${template.visualStyle}` : '');
      
      setNewItem({
        ...newItem,
        description: template.description,
        source: template.source,
        capabilities: template.capabilities,
        limitations: template.limitations,
        variants: template.variants,
        details: cinematicDetails,
        type: 'magic',
        magicType: template.name
      });
    }
  };

  // Function to apply tech template
  const applyTechTemplate = (templateName) => {
    const template = techTemplates.find(t => t.name === templateName);
    if (template) {
      // Build cinematic details from new fields
      const cinematicDetails = (template.details || '') + 
        (template.mood ? `\n\n**Mood:** ${template.mood}` : '') +
        (template.visualStyle ? `\n\n**Visual Style:** ${template.visualStyle}` : '');
      
      setNewItem({
        ...newItem,
        description: template.description,
        source: template.source,
        capabilities: template.capabilities,
        limitations: template.limitations,
        variants: template.variants,
        details: cinematicDetails,
        type: 'technology',
        techType: template.name
      });
    }
  };

  // Function to save the current item as a template
  const saveAsTemplate = () => {
    if (!newTemplateName.trim()) {
      return;
    }
    
    // Create a new template from the current item data
    const newTemplate = {
      name: `custom-${Date.now()}`,
      label: newTemplateName,
      icon: newItem.type === 'magic' 
        ? <AutoAwesomeIcon fontSize="small" />
        : <ScienceIcon fontSize="small" />,
      description: newTemplateDescription || 'Custom template',
      source: newItem.source || '',
      capabilities: newItem.capabilities || '',
      limitations: newItem.limitations || '',
      variants: newItem.variants || '',
      isCustom: true
    };
    
    if (newItem.type === 'magic') {
      setCustomMagicTemplates([...customMagicTemplates, newTemplate]);
    } else {
      setCustomTechTemplates([...customTechTemplates, newTemplate]);
    }
    
    setShowSaveTemplateDialog(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateIcon('');
  };
  
  // Function to apply a custom template
  const applyCustomTemplate = (templateName, type) => {
    const templates = type === 'magic' ? customMagicTemplates : customTechTemplates;
    const template = templates.find(t => t.name === templateName);
    
    if (template) {
      setNewItem({
        ...newItem,
        description: template.description,
        source: template.source,
        capabilities: template.capabilities,
        limitations: template.limitations,
        variants: template.variants,
        type: type
      });
    }
  };
  
  // Function to delete a custom template
  const deleteCustomTemplate = (templateName, type) => {
    if (type === 'magic') {
      setCustomMagicTemplates(customMagicTemplates.filter(t => t.name !== templateName));
    } else {
      setCustomTechTemplates(customTechTemplates.filter(t => t.name !== templateName));
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Tooltip title="Add new magic/tech system" arrow>
          <span>
            <IconButton
              onClick={() => handleOpenDialog()}
              aria-label="Add new magic/tech system"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                color: accentColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: accentBg
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="h5" component="h2" sx={{ 
          fontWeight: 600,
          color: accentColor
        }}>
          Magic & Technology
        </Typography>
      </Box>

      {magicSystems.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: `1px dashed ${accentBorder}`,
            bgcolor: isDarkMode ? 'rgba(103, 58, 183, 0.18)' : 'rgba(124, 77, 255, 0.08)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: accentColor }}>
            No magic or tech systems yet
          </Typography>
          <Typography variant="body2" color={textSecondary}>
            Tap the “+” icon to add your first system and power your world.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {magicSystems.map(item => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: cardBg,
                  border: `1px solid ${accentBorder}`,
                  boxShadow: isDarkMode 
                    ? '0 12px 32px rgba(0,0,0,0.5)'
                    : '0 10px 22px rgba(124, 77, 255, 0.15)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: isDarkMode 
                      ? '0 16px 36px rgba(0,0,0,0.6)'
                      : '0 18px 30px rgba(124, 77, 255, 0.25)'
                  }
                }}
              >
                <Box sx={{ 
                  p: 1, 
                  background: isDarkMode
                    ? 'linear-gradient(120deg, rgba(124,77,255,0.45), rgba(103,58,183,0.35))'
                    : 'linear-gradient(120deg, rgba(124,77,255,0.12), rgba(103,58,183,0.08))',
                  borderBottom: `1px solid ${accentBorder}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: textPrimary
                    }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.4,
                          borderRadius: '999px',
                          backgroundColor: chipBg,
                          border: `1px solid ${accentBorder}`,
                          color: accentColor,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        {item.type === 'magic' ? (
                          <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
                        ) : (
                          <ScienceIcon sx={{ fontSize: '1rem' }} />
                        )}
                        {item.type === 'magic' ? 'Magic' : 'Tech'}
                      </Box>
                      {item.importance && (
                        <Chip 
                          size="small" 
                          label={item.importance.charAt(0).toUpperCase() + item.importance.slice(1)} 
                          sx={{ 
                            fontWeight: 500,
                            height: '22px',
                            backgroundColor: chipBg,
                            color: textPrimary
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 2, flexGrow: 1, color: textSecondary }}>
                  {item.description && (
                    <Typography variant="body2" sx={{ mb: 1, color: textSecondary }}>
                      {item.description}
                    </Typography>
                  )}
                  
                  {item.source && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: textSecondary }}>
                        Source:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: textSecondary
                      }}>
                        {item.source}
                      </Typography>
                    </Box>
                  )}
                  
                  {item.capabilities && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: textSecondary }}>
                        Capabilities:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: textSecondary
                      }}>
                        {item.capabilities}
                      </Typography>
                    </Box>
                  )}
                  
                  {item.limitations && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: textSecondary }}>
                        Limitations:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: textSecondary
                      }}>
                        {item.limitations}
                      </Typography>
                    </Box>
                  )}
                  
                  {item.tags && item.tags.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            bgcolor: chipBg,
                            color: accentColor,
                            border: `1px solid ${accentBorder}`
                          }} 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                
                {/* Template Info */}
                {((item.type === 'magic' && item.magicType) || (item.type === 'technology' && item.techType)) && (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      mx: 2,
                      mb: 2,
                      borderRadius: 2, 
                      bgcolor: accentBg,
                      border: `1px dashed ${accentColor}`
                    }}
                  >
                    <Typography variant="caption" sx={{ 
                      color: accentColor 
                    }}>
                      {item.type === 'magic' 
                        ? magicTemplates.find(t => t.name === item.magicType)?.label || 'Magic'
                        : techTemplates.find(t => t.name === item.techType)?.label || 'Technology'
                      } Template
                    </Typography>
                  </Paper>
                )}
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  p: 1, 
                  pt: 0,
                  borderTop: `1px solid ${accentBorder}`
                }}>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />} 
                    onClick={() => handleOpenDialog(item)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<DeleteIcon />} 
                    color="error"
                    onClick={() => onDeleteMagic(item.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ProModal
        open={openDialog}
        onClose={handleCloseDialog}
        title={`${selectedItem ? 'Edit' : 'Add New'} ${newItem.type === 'magic' ? 'Magic' : 'Technology'} System`}
        eyebrow="MAGIC & TECH"
        icon={newItem.type === 'magic' ? <AutoAwesomeIcon /> : <ScienceIcon />}
        maxWidth="lg"
        actions={(
          <>
            <Button 
              onClick={handleCloseDialog}
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #c158dc 0%, #7c4dff 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ae52d4 0%, #5c6bc0 100%)'
                }
              }}
            >
              {selectedItem ? 'Update System' : 'Add System'}
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
              <AutoAwesomeIcon sx={{ mr: 1, fontSize: '1rem' }} /> Basic Information
            </Typography>
          
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="Name" 
                  value={newItem.name} 
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="importance-label">Importance</InputLabel>
                  <Select
                    labelId="importance-label"
                    value={newItem.importance}
                    label="Importance"
                    onChange={(e) => setNewItem({ ...newItem, importance: e.target.value })}
                  >
                    {importanceLevels.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Description" 
                  value={newItem.description} 
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  multiline
                  rows={2}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="type-label">Type</InputLabel>
                  <Select
                    labelId="type-label"
                    value={newItem.type}
                    label="Type"
                    onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                  >
                    <MenuItem value="magic">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AutoAwesomeIcon sx={{ mr: 1 }} />
                        Magic
                      </Box>
                    </MenuItem>
                    <MenuItem value="technology">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScienceIcon sx={{ mr: 1 }} />
                        Technology
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Magic Templates Section */}
          {newItem.type === 'magic' && (
            <Accordion 
              defaultExpanded={false}
              sx={{ 
                bgcolor: accentBg, 
                borderRadius: '1 !important', 
                border: `1px solid ${accentBorder}`,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                mt: 2,
                mb: 2
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                sx={{ 
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { my: 1 }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <AutoAwesomeIcon fontSize="small" sx={{ mr: 1 }} />
                  Quick Magic Templates
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {magicTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} key={template.name}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(124,77,255,0.25)'
                          },
                          border: newItem.magicType === template.name ? `2px solid ${accentColor}` : `1px solid ${accentBorder}`,
                          backgroundColor: isDarkMode ? 'rgba(33, 11, 54, 0.9)' : 'rgba(124,77,255,0.06)'
                        }}
                        onClick={() => applyMagicTemplate(template.name)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1.5,
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, rgba(193,88,220,0.4), rgba(124,77,255,0.6))',
                              color: '#fff',
                              boxShadow: '0 4px 12px rgba(124,77,255,0.35)'
                            }}>
                              {template.icon}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
                              {template.label}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: textSecondary }}>
                            {template.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Custom Magic Templates */}
                {customMagicTemplates.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1, color: accentColor }}>
                      Your Custom Magic Templates
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {customMagicTemplates.map((template) => (
                        <Grid item xs={12} sm={6} md={4} key={template.name}>
                          <Card sx={{ 
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 16px rgba(124,77,255,0.25)'
                            },
                            border: `1px solid ${accentBorder}`,
                            backgroundColor: isDarkMode ? 'rgba(29,10,42,0.95)' : 'rgba(124,77,255,0.04)'
                          }}>
                            <IconButton 
                              size="small" 
                              sx={{ 
                                position: 'absolute', 
                                top: 5, 
                                right: 5,
                                bgcolor: isDarkMode ? 'rgba(15,15,15,0.8)' : 'rgba(255,255,255,0.9)',
                                '&:hover': {
                                  bgcolor: isDarkMode ? 'rgba(15,15,15,0.95)' : '#fff',
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCustomTemplate(template.name, 'magic');
                              }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <CardContent onClick={() => applyCustomTemplate(template.name, 'magic')}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 1.5,
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, rgba(193,88,220,0.4), rgba(124,77,255,0.6))',
                                color: '#fff',
                                boxShadow: '0 4px 12px rgba(124,77,255,0.35)'
                              }}>
                                {template.icon}
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
                                {template.label}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: textSecondary }}>
                              {template.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          
          {/* Technology Templates Section */}
          {newItem.type === 'technology' && (
            <Accordion 
              defaultExpanded={false}
              sx={{ 
                bgcolor: accentBg, 
                borderRadius: '1 !important', 
                border: `1px solid ${accentBorder}`,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                mt: 2,
                mb: 2
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                sx={{ 
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { my: 1 }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <ScienceIcon fontSize="small" sx={{ mr: 1 }} />
                  Quick Technology Templates
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {techTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} key={template.name}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(124,77,255,0.25)'
                          },
                          border: newItem.techType === template.name ? `2px solid ${accentColor}` : `1px solid ${accentBorder}`,
                          backgroundColor: isDarkMode ? 'rgba(18, 15, 40, 0.95)' : 'rgba(98, 0, 234, 0.04)'
                        }}
                        onClick={() => applyTechTemplate(template.name)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1.5,
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, rgba(98,0,234,0.45), rgba(55,0,179,0.6))',
                              color: '#fff',
                              boxShadow: '0 4px 12px rgba(55,0,179,0.35)'
                            }}>
                              {template.icon}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
                              {template.label}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: textSecondary }}>
                            {template.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Custom Tech Templates */}
                {customTechTemplates.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1, color: accentColor }}>
                      Your Custom Technology Templates
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {customTechTemplates.map((template) => (
                        <Grid item xs={12} sm={6} md={4} key={template.name}>
                          <Card sx={{ 
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 16px rgba(55,0,179,0.25)'
                            },
                            border: `1px solid ${accentBorder}`,
                            backgroundColor: isDarkMode ? 'rgba(15, 8, 30, 0.95)' : 'rgba(55,0,179,0.04)'
                          }}>
                            <IconButton 
                              size="small" 
                              sx={{ 
                                position: 'absolute', 
                                top: 5, 
                                right: 5,
                                bgcolor: isDarkMode ? 'rgba(15,15,15,0.8)' : 'rgba(255,255,255,0.9)',
                                '&:hover': {
                                  bgcolor: isDarkMode ? 'rgba(15,15,15,0.95)' : '#fff',
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCustomTemplate(template.name, 'technology');
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <CardContent onClick={() => applyCustomTemplate(template.name, 'technology')}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1.5,
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, rgba(98,0,234,0.45), rgba(55,0,179,0.6))',
                                  color: '#fff',
                                  boxShadow: '0 4px 12px rgba(55,0,179,0.35)'
                                }}>
                                  {template.icon}
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
                                  {template.label}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ color: textSecondary }}>
                                {template.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          
          {[
            { key: 'source', label: 'Source', rows: 2, placeholder: 'Where does this power come from? The source often determines the cost and the story.' },
            { key: 'capabilities', label: 'Capabilities', rows: 2, placeholder: 'What can it do? Think visually—what would this look like on screen?' },
            { key: 'limitations', label: 'Limitations', rows: 2, placeholder: 'Every power needs limits. What creates tension? What can go wrong?' },
            { key: 'variants', label: 'Variants', rows: 2, placeholder: 'Different flavors or specializations. Each variant suggests different character types.' },
            { key: 'details', label: 'Cinematic Details', rows: 3, placeholder: 'Visual identity, key scenes, conflict hooks—how does this appear in your film?' }
          ].map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              value={newItem[field.key] || ''}
              onChange={(e) => setNewItem({ ...newItem, [field.key]: e.target.value })}
              multiline
              rows={field.rows}
              fullWidth
              placeholder={field.placeholder}
              InputLabelProps={{ sx: { color: accentColor } }}
              InputProps={{
                sx: {
                  color: textPrimary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: accentBorder },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                  borderRadius: 1,
                  backgroundColor: isDarkMode ? 'rgba(24,12,40,0.8)' : 'rgba(124,77,255,0.05)',
                  boxShadow: isDarkMode ? '0 0 0 rgba(0,0,0,0)' : '0 10px 30px rgba(124,77,255,0.09)'
                }
              }}
            />
          ))}
          
          <TextField
            label="Tags (comma separated)"
            value={newItem.tags ? newItem.tags.join(', ') : ''}
            onChange={(e) => {
              const tagsText = e.target.value;
              const tagsArray = tagsText.split(',').map(tag => tag.trim()).filter(tag => tag);
              setNewItem({ ...newItem, tags: tagsArray });
            }}
            fullWidth
            InputLabelProps={{ sx: { color: accentColor } }}
            InputProps={{
              sx: {
                color: textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: accentBorder },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                borderRadius: 1,
                backgroundColor: isDarkMode ? 'rgba(24,12,40,0.8)' : 'rgba(124,77,255,0.05)',
                boxShadow: isDarkMode ? '0 0 0 rgba(0,0,0,0)' : '0 10px 30px rgba(124,77,255,0.09)'
              }
            }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowSaveTemplateDialog(true)}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #c158dc 0%, #7c4dff 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ae52d4 0%, #5c6bc0 100%)'
                }
              }}
            >
              Save as Template
            </Button>
          </Box>
        </Box>
      </ProModal>

      {/* Save Template Dialog */}
      <ProModal
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title="Save as Template"
        eyebrow="MAGIC & TECH"
        icon={<BookmarkIcon />}
        maxWidth="sm"
        actions={(
          <>
            <Button 
              onClick={() => setShowSaveTemplateDialog(false)}
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={saveAsTemplate}
              disabled={!newTemplateName.trim()}
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #c158dc 0%, #7c4dff 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ae52d4 0%, #5c6bc0 100%)'
                }
              }}
            >
              Save Template
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This will save the current {newItem.type === 'magic' ? 'magic' : 'technology'} system as a template for future use.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField 
            label="Template Name" 
            value={newTemplateName} 
            onChange={(e) => setNewTemplateName(e.target.value)}
            fullWidth
            required
          />
          <TextField 
            label="Template Description" 
            value={newTemplateDescription} 
            onChange={(e) => setNewTemplateDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </ProModal>
    </Box>
  );
};

export default MagicTechEditor;
